import React, { useMemo, useState } from 'react';
import { FaPen, FaPlus, FaSearch, FaTimes } from 'react-icons/fa';
import { ReactComponent as CookbookIndexIcon } from '../../Icons/book.svg';
import IconRenderer from '../ui/IconRenderer';
import { formatLanguageDisplay } from '../../utils/roadmapDisplay';
import {
  filterPlaygroundSnippets,
  formatSnippetIndexDate,
  getSnippetCodePreview,
  normalizeSnippetTitle,
} from './playgroundSnippets';

const PlaygroundSnippetIndex = ({
  snippets,
  activeSnippetId,
  onSelectSnippet,
  onRenameSnippet,
  onDeleteSnippet,
  onNewPlayground,
}) => {
  const [query, setQuery] = useState('');

  const filteredSnippets = useMemo(
    () => filterPlaygroundSnippets(snippets, query),
    [snippets, query]
  );

  const handleDelete = (snippet) => {
    const confirmed = window.confirm(
      `Delete "${normalizeSnippetTitle(snippet.title)}"? This cannot be undone.`
    );
    if (confirmed) {
      onDeleteSnippet(snippet.id);
    }
  };

  return (
    <div className="playground-snippet-index">
      <div className="study-card__status-strip playground-snippet-index__status-strip">
        <span className="study-card__status-label playground-snippet-index__status-label">
          <span className="playground-snippet-index__status-leading">
            <span className="playground-snippet-index__status-icon-slot" aria-hidden="true">
              <CookbookIndexIcon
                className="playground-snippet-index__status-label-icon"
                focusable="false"
              />
            </span>
            <span className="playground-snippet-index__status-text">Playground Index</span>
          </span>
        </span>
      </div>

      <div className="playground-snippet-index__toolbar">
        <div className="playground-snippet-index__toolbar-cluster">
          <FaSearch className="playground-snippet-index__search-icon" aria-hidden />
          <input
            type="search"
            className="playground-snippet-index__search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search snippets…"
            aria-label="Search snippets"
          />
          <span className="playground-snippet-index__toolbar-divider" aria-hidden />
          <button
            type="button"
            className="playground-snippet-index__new-btn"
            onClick={onNewPlayground}
            title="Start a new empty playground"
          >
            <FaPlus className="playground-snippet-index__new-btn-icon" aria-hidden />
            <span className="playground-snippet-index__new-btn-label">New</span>
          </button>
        </div>
      </div>

      {filteredSnippets.length === 0 ? (
        <div className="playground-snippet-index__empty">
          {snippets.length === 0 ? (
            <>
              <p className="playground-snippet-index__empty-title">No saved snippets yet</p>
              <p className="playground-snippet-index__empty-hint">
                Write code in the editor, then use Save to keep it as a snippet.
              </p>
            </>
          ) : (
            <p className="playground-snippet-index__empty-hint">No snippets match your search.</p>
          )}
        </div>
      ) : (
        <div className="playground-snippet-index__list-scroll">
          <ul className="playground-snippet-index__list">
            {filteredSnippets.map((snippet) => {
              const isActive = snippet.id === activeSnippetId;
              const languageLabel = formatLanguageDisplay(snippet.language);
              const codePreview = getSnippetCodePreview(snippet.code, 96);
              const displayTitle = normalizeSnippetTitle(snippet.title);

              return (
                <li
                  key={snippet.id}
                  className={[
                    'playground-snippet-index__row',
                    isActive && 'playground-snippet-index__row--active',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <button
                    type="button"
                    className="playground-snippet-index__row-open"
                    onClick={() => onSelectSnippet(snippet.id)}
                    title={`${displayTitle} · ${languageLabel} · ${codePreview}`}
                  >
                    <span className="playground-snippet-index__row-leading" aria-hidden="true">
                      <IconRenderer
                        type="language"
                        name={snippet.language}
                        className="playground-snippet-index__row-lang-icon"
                      />
                    </span>
                    <span className="playground-snippet-index__row-body">
                      <span className="playground-snippet-index__row-title-text">
                        {displayTitle}
                      </span>
                      <code className="playground-snippet-index__row-preview">
                        {codePreview}
                      </code>
                    </span>
                  </button>

                  <div className="playground-snippet-index__row-aside">
                    <div className="playground-snippet-index__row-rail">
                      <time
                        className="playground-snippet-index__row-date"
                        dateTime={new Date(snippet.updatedAt).toISOString()}
                        title={`Updated ${new Date(snippet.updatedAt).toLocaleString()}`}
                      >
                        {formatSnippetIndexDate(snippet.updatedAt)}
                      </time>
                      <button
                        type="button"
                        className="playground-snippet-index__row-action"
                        onClick={() => onRenameSnippet(snippet.id)}
                        aria-label={`Rename ${displayTitle}`}
                        title="Rename"
                      >
                        <FaPen aria-hidden />
                      </button>
                      <button
                        type="button"
                        className="playground-snippet-index__row-action playground-snippet-index__row-action--delete"
                        onClick={() => handleDelete(snippet)}
                        aria-label={`Delete ${displayTitle}`}
                        title="Delete"
                      >
                        <FaTimes aria-hidden />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PlaygroundSnippetIndex;
